import {
  EMPTY_LIST,
  EMPTY_STRING,
  NOTIFICATION_TYPE,
} from '../../../../constants/common'
import i18n from '../../../../i18n'
import { toBuildingSelectItem } from '../../../Building'
import { transformSelectAreaCoordinatesToPercentCoordinates } from '../../../PdfViewer'
import { DocFlowWidgetStatus } from '../../DocFlow.constants'
import { DocFlowMappingWidgetWithSelectedAreaNotificationType } from '../../DocFlow.hooks'
import { getDocFlowSelectedAreaAccuracyInitialValue } from '../../DocFlow.utils.mapping'
import { isDocFlowWidgetStatusSkipped } from '../../DocFlow.utils.widgets'
import { DocFlowMappingProcessCell } from '../../DocFlowMapping'
import { DocFlowWidgetMappingType } from '../../DocFlowMappingWidgets'

import {
  DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_FOUND_ERROR_NOTIFICATION_ID,
  DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_FOUND_NOTIFICATION_ID,
  DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_NOT_CHANGED_ERROR_NOTIFICATION_ID,
  DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_NOTIFICATION_ID,
  DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_NOTIFICATION_ID_SERVER_ERROR,
  DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_TEST_IN_PROCESS_NOTIFICATION_ID,
  DOC_FLOW_MAPPING_WIDGET_MAPPING_IN_PROCESS_NOTIFICATION_ID,
} from './DocFlowMappingWidgetMapping.constants'

function getWidgetMappingSelectAreaTitleByType(type) {
  switch (type) {
    case DocFlowWidgetMappingType.ContractId:
      return 'DocumentStapling.WidgetMappingSelectAreaContractIdTitleKey'
    case DocFlowWidgetMappingType.Email:
      return 'DocumentStapling.WidgetMappingSelectAreaEmailTitleKey'
    case DocFlowWidgetMappingType.ExternalId:
      return 'DocumentStapling.WidgetMappingSelectAreaExternalIdTitleKey'
    case DocFlowWidgetMappingType.Name:
      return 'DocumentStapling.WidgetMappingSelectAreaNameTitleKey'
    case DocFlowWidgetMappingType.Phone:
      return 'DocumentStapling.WidgetMappingSelectAreaPhoneTitleKey'

    default:
      return null
  }
}

function getWidgetMappingUsers(users) {
  return users.reduce((acc, cur) => {
    return acc ? `${acc}\n${cur.fullname}` : cur.fullname
  }, EMPTY_STRING)
}

export function getMappingTypeOptions() {
  return [
    {
      label: i18n.t('DocumentStapling.MappingWidgetContractIdLabelKey'),
      value: DocFlowWidgetMappingType.ContractId,
    },
    {
      label: i18n.t('DocumentStapling.MappingWidgetNameLabelKey'),
      value: DocFlowWidgetMappingType.Name,
    },
  ]
}

export function getWidgetMappingInitialValueType(processData) {
  return !isDocFlowWidgetStatusSkipped(
    processData?.[DocFlowMappingProcessCell.MappingPhase]
  ) && processData?.[DocFlowMappingProcessCell.MappingType]
    ? getMappingTypeOptions().find(
        option =>
          option.value === processData?.[DocFlowMappingProcessCell.MappingType]
      )
    : null
}

export function getWidgetMappingInitialValues(processData, t) {
  return {
    [DocFlowMappingProcessCell.MappingBuilding]: processData?.[
      DocFlowMappingProcessCell.MappingBuilding
    ]
      ? toBuildingSelectItem(
          processData?.[DocFlowMappingProcessCell.MappingBuilding]
        )
      : null,
    [DocFlowMappingProcessCell.MappingBuildingGroup]: processData?.[
      DocFlowMappingProcessCell.MappingBuildingGroup
    ]
      ? toBuildingSelectItem(
          processData?.[DocFlowMappingProcessCell.MappingBuildingGroup]
        )
      : null,
    [DocFlowMappingProcessCell.MappingIncludeArchived]:
      processData?.[DocFlowMappingProcessCell.MappingIncludeArchived] ?? false,
    [DocFlowMappingProcessCell.MappingMatchAccuracy]:
      getDocFlowSelectedAreaAccuracyInitialValue(
        processData?.[DocFlowMappingProcessCell.MappingMatchAccuracy],
        t
      ),
    [DocFlowMappingProcessCell.MappingType]:
      getWidgetMappingInitialValueType(processData),
  }
}

export function getWidgetMappingProcessPayload({
  isChanged,
  pdfPageCoordinates,
  previous,
  selectedBox,
  values,
}) {
  const payload = {}

  const type = values[DocFlowMappingProcessCell.MappingType]?.value
  const accuracy = values[DocFlowMappingProcessCell.MappingMatchAccuracy].value
  const area =
    type && selectedBox
      ? transformSelectAreaCoordinatesToPercentCoordinates(
          selectedBox,
          pdfPageCoordinates
        )
      : EMPTY_LIST
  const building =
    values[DocFlowMappingProcessCell.MappingBuilding]?.value ||
    values[DocFlowMappingProcessCell.MappingBuilding]

  const buildingGroup =
    values[DocFlowMappingProcessCell.MappingBuildingGroup]?.value ||
    values[DocFlowMappingProcessCell.MappingBuildingGroup]

  const isBuildingExist =
    isDocFlowWidgetMappingTypeContractId(type) ||
    isDocFlowWidgetMappingTypeName(type)

  if (type) {
    payload[DocFlowMappingProcessCell.MappingType] = type
    payload[DocFlowMappingProcessCell.MappingMatchAccuracy] = accuracy
    payload[DocFlowMappingProcessCell.MappingArea] = area

    if (isBuildingExist) {
      payload[DocFlowMappingProcessCell.MappingBuilding] = building || null
      payload[DocFlowMappingProcessCell.MappingBuildingGroup] =
        buildingGroup || null

      payload[DocFlowMappingProcessCell.MappingIncludeArchived] =
        values[DocFlowMappingProcessCell.MappingIncludeArchived]
    }

    if (!previous) {
      payload[DocFlowMappingProcessCell.MappingPhase] =
        DocFlowWidgetStatus.Completed
      payload[DocFlowMappingProcessCell.ResultPhase] =
        DocFlowWidgetStatus.InProgress
    }
  } else if (!previous) {
    payload[DocFlowMappingProcessCell.MappingPhase] =
      DocFlowWidgetStatus.Skipped
    payload[DocFlowMappingProcessCell.ResultPhase] =
      DocFlowWidgetStatus.InProgress
  }

  if (previous) {
    if (!type) {
      payload[DocFlowMappingProcessCell.MappingMatchAccuracy] = accuracy
      payload[DocFlowMappingProcessCell.MappingArea] = area
      payload[DocFlowMappingProcessCell.MappingType] = null
    }
  }

  if (!isChanged && payload[DocFlowMappingProcessCell.MappingArea]) {
    delete payload[DocFlowMappingProcessCell.MappingArea]
  }

  return payload
}

export function getWidgetMappingSelectedAreaInfoData(data) {
  if (data.value) {
    return [
      {
        text: data.value,
        title: i18n.t(
          getWidgetMappingSelectAreaTitleByType(
            data[DocFlowMappingProcessCell.MappingType]
          )
        ),
      },
      {
        text: getWidgetMappingUsers(data.recipients),
        title: i18n.t('DocumentStapling.WidgetMappingSelectAreaUserTitleKey'),
      },
    ]
  }

  return EMPTY_LIST
}

export function getWidgetMappingSelectedAreaPayload(value) {
  const payload = {
    [DocFlowMappingProcessCell.MappingIncludeArchived]:
      value[DocFlowMappingProcessCell.MappingIncludeArchived],
    [DocFlowMappingProcessCell.MappingType]:
      value[DocFlowMappingProcessCell.MappingType].value,
    accuracy: value[DocFlowMappingProcessCell.MappingMatchAccuracy].value,
  }

  if (value[DocFlowMappingProcessCell.MappingBuilding]) {
    payload[DocFlowMappingProcessCell.MappingBuilding] =
      value[DocFlowMappingProcessCell.MappingBuilding].value ||
      value[DocFlowMappingProcessCell.MappingBuilding]
  }

  if (value[DocFlowMappingProcessCell.MappingBuildingGroup]) {
    payload[DocFlowMappingProcessCell.MappingBuildingGroup] =
      value[DocFlowMappingProcessCell.MappingBuildingGroup].value ||
      value[DocFlowMappingProcessCell.MappingBuildingGroup]
  }

  return payload
}

export function isMappingArea(values) {
  return values[DocFlowMappingProcessCell.MappingType]?.value
}

export function getWidgetMappingNotifications(t) {
  return {
    [DocFlowMappingWidgetWithSelectedAreaNotificationType.AreaFound]: {
      id: DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_FOUND_NOTIFICATION_ID,
      text: t('DocumentStapling.MappingSelectedAreaFoundNotificationTitleKey'),
    },
    [DocFlowMappingWidgetWithSelectedAreaNotificationType.AreaNotFound]: {
      id: DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_FOUND_ERROR_NOTIFICATION_ID,
      text: t(
        'DocumentStapling.MappingSelectedAreaFoundErrorNotificationTitleKey'
      ),
    },
    [DocFlowMappingWidgetWithSelectedAreaNotificationType.MappingInProcess]: {
      id: DOC_FLOW_MAPPING_WIDGET_MAPPING_IN_PROCESS_NOTIFICATION_ID,
      text: t('DocFlow.MappingWidgetMappingInProcessNotificationTitleKey'),
      type: NOTIFICATION_TYPE.info,
    },
    [DocFlowMappingWidgetWithSelectedAreaNotificationType.NotChanged]: {
      id: DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_NOT_CHANGED_ERROR_NOTIFICATION_ID,
      text: t(
        'DocumentStapling.MappingSelectedAreaNotChangedNotificationTitleKey'
      ),
    },
    [DocFlowMappingWidgetWithSelectedAreaNotificationType.Phase]: {
      id: DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_NOTIFICATION_ID,
      text: t('DocumentStapling.MappingWidgetAreaNotificationTitleKey'),
    },
    [DocFlowMappingWidgetWithSelectedAreaNotificationType.ServerError]: {
      id: DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_NOTIFICATION_ID_SERVER_ERROR,
      text: t('DocumentStapling.MappingSelectedAreaFoundNotificationTitleKey'),
    },
    [DocFlowMappingWidgetWithSelectedAreaNotificationType.TestInProcess]: {
      id: DOC_FLOW_MAPPING_WIDGET_MAPPING_AREA_TEST_IN_PROCESS_NOTIFICATION_ID,
      text: t(
        'DocumentStapling.MappingWidgetAreaTestInProcessNotificationTitleKey'
      ),
    },
  }
}

export function isDocFlowWidgetMappingTypeContractId(value) {
  return value === DocFlowWidgetMappingType.ContractId
}

export function isDocFlowWidgetMappingTypeName(value) {
  return value === DocFlowWidgetMappingType.Name
}
